/**
 * Students List Page — Shows students belonging to a selected entity.
 * Admin/Staff view.
 */

import { useParams, Link } from 'react-router-dom';
import { useStudents } from '@/lib/hooks/useStudents';
import { useEntityName } from '@/lib/EntityContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { ChevronRight, Mail, BookOpen } from 'lucide-react';

export function StudentsListPage() {
  const { entityId } = useParams<{ entityId: string }>();
  const { students, entity, loading, error } = useStudents(entityId);
  const entityName = useEntityName(entityId) || entity?.name;

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active': return <Badge variant="success">Active</Badge>;
      case 'inactive': return <Badge variant="secondary">Inactive</Badge>;
      case 'withdrawn': return <Badge variant="destructive">Withdrawn</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading students</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64" role="alert">
        <p className="text-destructive text-sm font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <nav aria-label="Breadcrumb" className="flex items-center text-[11px] font-bold tracking-widest text-muted-foreground uppercase mb-6 animate-fade-up">
        <Link to="/entities" className="hover:text-primary transition-colors">Entities</Link>
        <span className="mx-2 w-[1px] h-3 bg-surface-highest inline-block" aria-hidden="true" />
        <span className="text-primary" aria-current="page">{entityName || 'Students'}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">{entityName || 'Students'}</h2>
          <p className="text-muted-foreground mt-1">{students.length} student records</p>
        </div>
      </div>

      {students.length === 0 ? (
        <div className="bg-white rounded-3xl p-16 text-center shadow-sm animate-fade-up-delay-1">
          <p className="text-muted-foreground text-sm">No students found for this entity.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-sm overflow-hidden animate-fade-up-delay-1">
          {/* Mobile card layout */}
          <div className="lg:hidden divide-y divide-surface-low">
            {students.map((student) => (
              <Link
                key={student.id}
                to={`/entities/${entityId}/students/${student.id}`}
                className="flex items-center gap-4 p-4 hover:bg-surface-low/50 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-indigo-500 flex items-center justify-center text-white text-xs font-bold shadow-sm shrink-0">
                  {student.firstName[0]}{student.lastName[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-bold text-foreground truncate">{student.firstName} {student.lastName}</p>
                    {statusBadge(student.status)}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{student.email}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] font-mono text-muted-foreground">{student.studentId}</span>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <BookOpen className="w-3 h-3" aria-hidden="true" /> {student.contactCount} <span className="sr-only">contacts</span>
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-surface-highest shrink-0" aria-hidden="true" />
              </Link>
            ))}
          </div>

          {/* Desktop table layout */}
          <div className="hidden lg:block">
          <Table>
            <TableHeader>
              <TableRow className="bg-surface-low/50">
                <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Student</TableHead>
                <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Student ID</TableHead>
                <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Email</TableHead>
                <TableHead className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Status</TableHead>
                <TableHead className="text-center text-[11px] font-bold text-muted-foreground uppercase tracking-widest">Contacts</TableHead>
                <TableHead className="text-right text-[11px] font-bold text-muted-foreground uppercase tracking-widest" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {students.map((student) => (
                <TableRow key={student.id} className="hover:bg-surface-low/50 transition-colors group">
                  <TableCell className="py-5">
                    <Link to={`/entities/${entityId}/students/${student.id}`} className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-indigo-500 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                        {student.firstName[0]}{student.lastName[0]}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground">{student.firstName} {student.lastName}</p>
                        <p className="text-[10px] text-muted-foreground">Enrolled {new Date(student.enrollmentDate).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}</p>
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs font-mono text-muted-foreground">{student.studentId}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Mail className="w-3.5 h-3.5" aria-hidden="true" />
                      {student.email}
                    </div>
                  </TableCell>
                  <TableCell>{statusBadge(student.status)}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
                      <span className="text-sm font-semibold text-foreground">{student.contactCount}</span>
                      <span className="sr-only">contacts</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Link
                      to={`/entities/${entityId}/students/${student.id}`}
                      className="p-2 rounded-lg text-surface-highest group-hover:text-primary transition-colors inline-flex"
                      aria-label={`View profile for ${student.firstName} ${student.lastName}`}
                    >
                      <ChevronRight className="w-5 h-5" aria-hidden="true" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <div className="px-6 py-4 bg-surface-low/30" style={{ borderTop: '1px solid hsl(210 12% 96%)' }}>
            <span className="text-sm text-muted-foreground font-medium">
              Showing <span className="text-foreground">{students.length}</span> students
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
